import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, api } from "@/lib/auth";
import { toast } from "sonner";
import * as Icons from "lucide-react";

function Icon({ name, ...p }) { const C = Icons[name] || Icons.Circle; return <C {...p} />; }

const GOALS = ["Digital lead generation", "Grow SIP book", "Acquire HNI clients", "Client retention", "Build personal brand", "Increase AUM"];
const AUM_RANGES = ["Under ₹10 Cr", "₹10-50 Cr", "₹50-100 Cr", "₹100-250 Cr", "₹250 Cr+"];
const MATURITY = ["Just starting", "Some tools", "Fairly digital", "Highly digital"];
const TOOLS = ["Excel", "WhatsApp", "Instagram", "LinkedIn", "Google Business", "A CRM"];
const LANGS = ["English", "Hindi", "Marathi", "Gujarati", "Tamil", "Telugu", "Kannada", "Bengali"];

export default function Onboarding() {
  const { user, setUser } = useAuth();
  const nav = useNavigate();
  const [step, setStep] = useState(0);
  const [d, setD] = useState({ arn: "", location: "", years: "", aum_range: "", clients: "", goals: [], digital_maturity: "", tools: [], languages: [] });
  const [done, setDone] = useState(false);

  if (!user) { nav("/login"); return null; }
  const toggle = (k, v) => setD({ ...d, [k]: d[k].includes(v) ? d[k].filter((x) => x !== v) : [...d[k], v] });

  const steps = [
    { title: "The basics", body: (
      <div className="space-y-4">
        <Field label="ARN number"><input data-testid="ob-arn" value={d.arn} onChange={(e) => setD({ ...d, arn: e.target.value })} placeholder="ARN-XXXXXX" className={inp} /></Field>
        <Field label="Location"><input data-testid="ob-location" value={d.location} onChange={(e) => setD({ ...d, location: e.target.value })} placeholder="Pune, Maharashtra" className={inp} /></Field>
        <Field label="Years in business"><input data-testid="ob-years" value={d.years} onChange={(e) => setD({ ...d, years: e.target.value })} placeholder="8" className={inp} /></Field>
      </div>
    )},
    { title: "Your practice", body: (
      <div className="space-y-4">
        <Field label="AUM range"><Chips options={AUM_RANGES} sel={[d.aum_range]} onPick={(v) => setD({ ...d, aum_range: v })} /></Field>
        <Field label="Number of clients"><input data-testid="ob-clients" value={d.clients} onChange={(e) => setD({ ...d, clients: e.target.value })} placeholder="420" className={inp} /></Field>
        <Field label="Digital maturity"><Chips options={MATURITY} sel={[d.digital_maturity]} onPick={(v) => setD({ ...d, digital_maturity: v })} /></Field>
      </div>
    )},
    { title: "Your goals", body: (
      <div className="space-y-4">
        <Field label="Business goals (pick a few)"><Chips options={GOALS} sel={d.goals} onPick={(v) => toggle("goals", v)} multi /></Field>
        <Field label="Current tools"><Chips options={TOOLS} sel={d.tools} onPick={(v) => toggle("tools", v)} multi /></Field>
        <Field label="Preferred languages"><Chips options={LANGS} sel={d.languages} onPick={(v) => toggle("languages", v)} multi /></Field>
      </div>
    )},
  ];

  const finish = async () => {
    try { const u = await api.post("/auth/onboarding", d); setUser(u.data); setDone(true); }
    catch { toast.error("Could not save. Try again."); }
  };

  if (done) return (
    <div className="min-h-screen bg-[#F8E3D0] grid place-items-center p-6">
      <div className="w-full max-w-lg card-mfx p-8 fade-up">
        <div className="w-14 h-14 rounded-2xl grad-lime-cyan grid place-items-center mb-5"><Icon name="Rocket" size={28} className="text-white" /></div>
        <span className="text-xs uppercase tracking-widest text-gray-400 font-semibold">Your MFDX Growth Plan</span>
        <h1 className="font-head text-3xl font-bold mt-2">Your biggest opportunity: <span className="gradient-text">digital lead generation.</span></h1>
        <p className="text-gray-500 mt-2">Based on your profile, here's where to start:</p>
        <ol className="mt-5 space-y-3">
          {["Create your Google Business Profile", "Build your MFD website with AI", "Complete the Digital Marketing course", "Launch weekly LinkedIn content", "Activate your CRM & import leads", "Start a Google review campaign"].map((s, i) => (
            <li key={s} className="flex items-center gap-3">
              <span className="w-7 h-7 rounded-full grad-blue-violet text-white grid place-items-center text-sm font-bold shrink-0">{i + 1}</span>
              <span className="text-gray-700">{s}</span>
            </li>
          ))}
        </ol>
        <button onClick={() => nav("/app")} data-testid="ob-enter-btn" className="mt-7 w-full py-3.5 rounded-xl grad-blue-violet text-white font-semibold hover-lift flex items-center justify-center gap-2">
          Enter my MFDX workspace <Icon name="ArrowRight" size={18} />
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#F8E3D0] grid place-items-center p-6">
      <div className="w-full max-w-lg card-mfx p-8">
        <div className="flex items-center gap-2 mb-6">
          {steps.map((s, i) => <div key={s.title} className={`h-1.5 flex-1 rounded-full ${i <= step ? "grad-blue-violet" : "bg-gray-100"}`} />)}
        </div>
        <span className="text-xs uppercase tracking-widest text-gray-400 font-semibold">Step {step + 1} of {steps.length}</span>
        <h1 className="font-head text-2xl font-bold mt-1 mb-5">{steps[step].title}</h1>
        {steps[step].body}
        <div className="mt-7 flex gap-3">
          {step > 0 && <button onClick={() => setStep(step - 1)} className="px-5 py-3 rounded-xl border border-gray-200 font-semibold text-gray-700">Back</button>}
          <button onClick={() => step < steps.length - 1 ? setStep(step + 1) : finish()} data-testid="ob-next-btn"
            className="flex-1 py-3 rounded-xl grad-blue-violet text-white font-semibold hover-lift flex items-center justify-center gap-2">
            {step < steps.length - 1 ? "Continue" : "Generate my Growth Plan"} <Icon name="ArrowRight" size={18} />
          </button>
        </div>
      </div>
    </div>
  );
}

const inp = "w-full rounded-xl border border-gray-200 px-4 py-3 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 outline-none";
function Field({ label, children }) { return <div><label className="text-sm font-medium text-gray-700 block mb-1.5">{label}</label>{children}</div>; }
function Chips({ options, sel, onPick, multi }) {
  return <div className="flex flex-wrap gap-2">{options.map((o) => (
    <button key={o} type="button" onClick={() => onPick(o)}
      className={`px-3.5 py-2 rounded-full text-sm font-medium border transition-colors ${sel.includes(o) ? "grad-blue-violet text-white border-transparent" : "bg-white border-gray-200 text-gray-600 hover:border-blue-300"}`}>
      {o}{multi && sel.includes(o) ? " ✓" : ""}
    </button>
  ))}</div>;
}
